import { AppSelect } from '../ui/AppSelect';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Image as ImageIcon, Film, Grid, List, Check, Layers, Box, Monitor, FolderOpen } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useDelayedUnmount } from '../../hooks/useDelayedUnmount';
import { authenticatedFetch } from '../../utils/api';
import { apiPost } from '../../utils/apiAdapter';
import { API_HOST as CONST_API_HOST } from '../../utils/constants';

/**
 * 资源选择器组件 - 标准渲染版 (移除懒加载，修复无限刷新和布局问题)
 */
const ResourceSelector = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  projectId, 
  type = 'all',
  title = '选择资源',
  mode = 'selector',
  onPreview
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const modalRef = useRef(null);
  
  // 数据状态
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // 筛选状态
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');
  const [activeSourceFilter, setActiveSourceFilter] = useState('all');
  const [activeEpisodeFilter, setActiveEpisodeFilter] = useState('all');
  
  // 视图状态
  const [viewMode, setViewMode] = useState('grid');
  const [selectedResource, setSelectedResource] = useState(null);
  
  // 集数列表
  const [episodes, setEpisodes] = useState([]);
  
  // 布局参数
  const ITEM_HEIGHT = 160;

  // 资源类型选项
  const typeFilterOptions = [
    { id: 'all', label: '全部', icon: Grid },
    { id: 'storyboard_image', label: '分镜图片', icon: ImageIcon },
    { id: 'storyboard_video', label: '分镜视频', icon: Film },
    { id: 'character_image', label: '角色', icon: Layers },
    { id: 'scene_image', label: '场景', icon: Box },
    { id: 'prop_image', label: '道具', icon: Box },
    { id: 'agent_image', label: 'AI图片', icon: ImageIcon },
    { id: 'agent_video', label: 'AI视频', icon: Film },
  ];

  // 来源模块选项
  const sourceFilterOptions = [
    { id: 'all', label: '全部来源', icon: Grid },
    { id: 'storyboard', label: '分镜模块', icon: Monitor },
    { id: 'image_agent', label: '图片Agent', icon: ImageIcon },
    { id: 'video_agent', label: '视频Agent', icon: Film },
    { id: 'character', label: '角色库', icon: Layers },
    { id: 'scene', label: '场景库', icon: Box },
    { id: 'prop', label: '道具库', icon: Box },
  ];

  // 根据外部 type prop 初始化过滤
  useEffect(() => {
    if (type === 'image') {
      setActiveTypeFilter('all');
    } else if (type === 'video') {
      setActiveTypeFilter('storyboard_video');
    } else {
      setActiveTypeFilter('all');
    }
  }, [type, isOpen]);

  // 加载资源和集数
  useEffect(() => {
    if ((isOpen || mode === 'gallery') && projectId) {
      loadResources();
      loadEpisodes();
    }
  }, [isOpen, projectId, mode]);

  // 加载集数列表
  const loadEpisodes = async () => {
    try {
      const data = await authenticatedFetch(`${CONST_API_HOST}/api/project/episode/list?projectId=${projectId}`);
      setEpisodes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('加载集数失败:', error);
      setEpisodes([]);
    }
  };

  // 过滤后的资源
  const filteredResources = useMemo(() => {
    let filtered = resources;
    if (activeTypeFilter !== 'all') filtered = filtered.filter(r => r.typeCode === activeTypeFilter);
    if (activeSourceFilter !== 'all') filtered = filtered.filter(r => r.source === activeSourceFilter);
    if (activeEpisodeFilter !== 'all') filtered = filtered.filter(r => String(r.episodeId) === String(activeEpisodeFilter));
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(r => 
        (r.name && r.name.toLowerCase().includes(keyword)) ||
        (r.description && r.description.toLowerCase().includes(keyword)) ||
        (r.prompt && r.prompt.toLowerCase().includes(keyword))
      );
    }
    return filtered;
  }, [resources, activeTypeFilter, activeSourceFilter, activeEpisodeFilter, searchKeyword]);

  const loadResources = async () => {
    setIsLoading(true);
    try {
      // 1. 从 unified_resource 表加载
      const data = await apiPost(`${CONST_API_HOST}/api/resource/query`, {
        projectId: parseInt(projectId)
      });
      const uniqueData = Array.from(new Map((data || []).map(item => [item.id, item])).values())
        .map(item => ({
          ...item,
          resourceId: item.resourceId ?? item.id,
        }));

      // 2. 从角色/场景/道具工坊加载
      const [characters, scenes, props] = await Promise.all([
        authenticatedFetch(`${CONST_API_HOST}/api/characters?projectId=${projectId}`).catch(() => []),
        authenticatedFetch(`${CONST_API_HOST}/api/scenes?projectId=${projectId}`).catch(() => []),
        authenticatedFetch(`${CONST_API_HOST}/api/props?projectId=${projectId}`).catch(() => []),
      ]);

      const workshopResources = [];
      (characters || []).forEach(c => {
        if (c.imageUrl) {
          workshopResources.push({
            id: `char_${c.id}`, url: c.imageUrl, thumbnailUrl: c.imageUrl,
            name: c.name || '角色', typeCode: 'character_image', source: 'character',
            sourceLabel: '角色库', prompt: c.description,
          });
        }
        // 角色变体
        (c.variations || []).forEach((v, i) => {
          if (v.imageUrl) {
            workshopResources.push({
              id: `char_${c.id}_v${i}`, url: v.imageUrl, thumbnailUrl: v.imageUrl,
              name: `${c.name || '角色'} - ${v.label || '变体' + (i + 1)}`,
              typeCode: 'character_image', source: 'character', sourceLabel: '角色库',
            });
          }
        });
      });
      (scenes || []).forEach(s => {
        if (s.imageUrl) {
          workshopResources.push({
            id: `scene_${s.id}`, url: s.imageUrl, thumbnailUrl: s.imageUrl,
            name: s.name || '场景', typeCode: 'scene_image', source: 'scene',
            sourceLabel: '场景库', prompt: s.description,
          });
        }
        (s.variations || []).forEach((v, i) => {
          if (v.imageUrl) {
            workshopResources.push({
              id: `scene_${s.id}_v${i}`, url: v.imageUrl, thumbnailUrl: v.imageUrl,
              name: `${s.name || '场景'} - ${v.label || '变体' + (i + 1)}`,
              typeCode: 'scene_image', source: 'scene', sourceLabel: '场景库',
            });
          }
        });
      });
      (props || []).forEach(p => {
        if (p.imageUrl) {
          workshopResources.push({
            id: `prop_${p.id}`, url: p.imageUrl, thumbnailUrl: p.imageUrl,
            name: p.name || '道具', typeCode: 'prop_image', source: 'prop',
            sourceLabel: '道具库', prompt: p.description,
          });
        }
        (p.variations || []).forEach((v, i) => {
          if (v.imageUrl) {
            workshopResources.push({
              id: `prop_${p.id}_v${i}`, url: v.imageUrl, thumbnailUrl: v.imageUrl,
              name: `${p.name || '道具'} - ${v.label || '变体' + (i + 1)}`,
              typeCode: 'prop_image', source: 'prop', sourceLabel: '道具库',
            });
          }
        });
      });

      setResources([...uniqueData, ...workshopResources]);
    } catch (error) {
      console.error('加载资源失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mode === 'selector' && modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, mode]);

  const handleSelect = (resource) => {
    if (mode === 'gallery') {
      if (onPreview) onPreview(resource.url || resource.thumbnailUrl);
    } else {
      setSelectedResource(resource);
    }
  };

  const handleConfirm = () => {
    if (selectedResource) {
      onSelect(selectedResource);
      onClose();
    }
  };

  const isSelector = mode === 'selector';
  const m = useDelayedUnmount(!isSelector || isOpen, 150);

  if (isSelector && !m.mounted) return null;

  const content = (
    <div className={isSelector ? `fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[var(--overlay-medium)] backdrop-blur-sm ${m.exiting ? 'animate-out fade-out duration-150' : 'animate-in fade-in duration-200'}` : "h-full w-full bg-transparent"}>
      <div ref={modalRef} className={`flex flex-col overflow-hidden ${isSelector ? `w-full max-w-6xl h-[85vh] rounded-xl border shadow-2xl ${m.exiting ? 'animate-out fade-out zoom-out-95 duration-150' : 'animate-in fade-in zoom-in-95 duration-200'}` : 'w-full h-full'}`}
        style={isSelector ? { backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' } : {}}>

      {isSelector && (
        <div className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
          <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <FolderOpen size={16} style={{ color: theme.colors.accentPrimary }} />
            {title}
          </h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }} className="hover:text-[var(--color-text-primary)] transition-colors"><X size={18} /></button>
        </div>
      )}

      <div className={`px-4 py-3 space-y-3 shrink-0 ${mode === 'gallery' ? '' : 'bg-opacity-50'}`}
        style={mode === 'selector' ? { borderBottom: '1px solid var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)' } : {}}>
        
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
            <input
              type="text" value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder="搜索资源名称、描述、提示词..."
              className="w-full pl-9 pr-3 py-2 rounded-lg text-xs outline-none"
              style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
            />
          </div>
          <div className="flex-1"></div>
          <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
            <button onClick={() => setViewMode('grid')} className="p-1.5 rounded" style={{ color: viewMode === 'grid' ? theme.colors.accentPrimary : 'var(--color-text-muted)', backgroundColor: viewMode === 'grid' ? 'var(--color-bg-overlay)' : 'transparent' }}><Grid size={14} /></button>
            <button onClick={() => setViewMode('list')} className="p-1.5 rounded" style={{ color: viewMode === 'list' ? theme.colors.accentPrimary : 'var(--color-text-muted)', backgroundColor: viewMode === 'list' ? 'var(--color-bg-overlay)' : 'transparent' }}><List size={14} /></button>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-2xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>来源</span>
            <div className="flex items-center gap-1">
              {sourceFilterOptions.map(option => (
                <button key={option.id} onClick={() => setActiveSourceFilter(option.id)} className="px-2 py-1 rounded-md text-2xs font-medium transition-all flex items-center gap-1"
                  style={{ backgroundColor: activeSourceFilter === option.id ? theme.colors.accentPrimary : 'var(--color-bg-primary)', color: activeSourceFilter === option.id ? 'white' : 'var(--color-text-secondary)', border: `1px solid ${activeSourceFilter === option.id ? theme.colors.accentPrimary : 'var(--color-border-primary)'}` }}>
                  <option.icon size={10} />{option.label}
                </button>
              ))}
            </div>
          </div>
          {episodes.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-2xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>集数</span>
              <AppSelect value={activeEpisodeFilter} onChange={(e) => setActiveEpisodeFilter(e.target.value)} className="px-2 py-1 rounded-md text-2xs outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: `1px solid ${activeEpisodeFilter !== 'all' ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`,
                  color: 'var(--color-text-primary)',
                }}>
                <option value="all" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}>全部集数</option>
                {episodes.map(ep => <option key={ep.id} value={ep.id} style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}>{ep.name}</option>)}
              </AppSelect>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-2xs uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>类型</span>
            <div className="flex items-center gap-1">
              {typeFilterOptions.slice(0, 5).map(option => (
                <button key={option.id} onClick={() => setActiveTypeFilter(option.id)} className="px-2 py-1 rounded-md text-2xs font-medium transition-all flex items-center gap-1"
                  style={{ backgroundColor: activeTypeFilter === option.id ? theme.colors.bluePrimary : 'var(--color-bg-primary)', color: activeTypeFilter === option.id ? 'white' : 'var(--color-text-secondary)', border: `1px solid ${activeTypeFilter === option.id ? theme.colors.bluePrimary : 'var(--color-border-primary)'}` }}>
                  <option.icon size={10} />{option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {isLoading ? (
          <div className="flex items-center justify-center h-full" style={{ color: 'var(--color-text-muted)' }}>加载中...</div>
        ) : filteredResources.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full" style={{ color: 'var(--color-text-muted)' }}>
            <ImageIcon size={48} opacity={0.3} /><p className="mt-4 text-sm">暂无资源</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {filteredResources.map((resource) => {
              const isSelected = selectedResource?.id === resource.id;
              return (
                <div key={resource.id} onClick={() => handleSelect(resource)} className="relative group cursor-pointer rounded-lg overflow-hidden transition-shadow hover:shadow-lg aspect-square"
                  style={{ border: `2px solid ${isSelected && mode === 'selector' ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`, backgroundColor: 'var(--color-bg-overlay)' }}>
                  {resource.typeCode?.includes('video') ? (
                    resource.thumbnailUrl ? (
                      <img src={resource.thumbnailUrl} alt={resource.name} className="w-full h-full object-contain" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                        <Film size={32} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                      </div>
                    )
                  ) : (
                    <img src={resource.thumbnailUrl || resource.url} alt={resource.name} className="w-full h-full object-contain" loading="lazy" />
                  )}
                  {resource.typeCode?.includes('video') && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-medium)' }}><Film size={14} className="text-white" /></div></div>
                  )}
                  {isSelected && mode === 'selector' && (
                    <div className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: theme.colors.accentPrimary }}><Check size={12} className="text-white" /></div>
                  )}
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-2xs" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{resource.sourceLabel || resource.source}</div>
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 text-2xs truncate" style={{ backgroundColor: 'var(--overlay-heavy)', color: 'white' }}>{resource.typeLabel}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredResources.map((resource) => {
              const isSelected = selectedResource?.id === resource.id;
              return (
                <div key={resource.id} onClick={() => handleSelect(resource)} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all"
                  style={{ backgroundColor: isSelected && mode === 'selector' ? `${theme.colors.accentPrimary}20` : 'var(--color-bg-primary)', border: `1px solid ${isSelected && mode === 'selector' ? theme.colors.accentPrimary : 'var(--color-border-primary)'}` }}>
                  {resource.typeCode?.includes('video') ? (
                    resource.thumbnailUrl ? (
                      <img src={resource.thumbnailUrl} alt={resource.name} className="w-12 h-12 rounded object-cover" style={{ backgroundColor: 'var(--color-bg-overlay)' }} />
                    ) : (
                      <div className="w-12 h-12 rounded flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                        <Film size={16} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                      </div>
                    )
                  ) : (
                    <img src={resource.thumbnailUrl || resource.url} alt={resource.name} className="w-12 h-12 rounded object-cover" style={{ backgroundColor: 'var(--color-bg-overlay)' }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{resource.name}</div>
                    <div className="text-2xs truncate" style={{ color: 'var(--color-text-muted)' }}>{resource.sourceLabel} · {resource.typeLabel}</div>
                  </div>
                  {isSelected && mode === 'selector' && <Check size={16} style={{ color: theme.colors.accentPrimary }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {mode === 'selector' && (
        <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderTop: '1px solid var(--color-border-primary)' }}>
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>共 {filteredResources.length} 个资源</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}>取消</button>
            <button onClick={handleConfirm} disabled={!selectedResource} className="px-4 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>确认选择</button>
          </div>
        </div>
      )}
      </div>
    </div>
  );

  return mode === 'selector' ? createPortal(content, document.body) : content;
};

export default ResourceSelector;
